<template>
  <div class="settings">
    <div class="header">
      <h1 class="title">设置</h1>
    </div>

    <div class="content">
      <!-- 账号信息 -->
      <div class="section">
        <div class="section-header">账号信息</div>
        <van-cell-group inset class="cell-group">
          <van-cell 
            :title="currentUser?.get('username')" 
            :label="currentUser?.get('email')"
            center
            class="user-cell"
          >
            <template #icon>
              <div class="avatar">
                <van-icon name="user-circle-o" size="32" color="#007AFF"/>
              </div>
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <!-- 数据管理 -->
      <div class="section">
        <div class="section-header">数据管理</div>
        <van-cell-group inset class="cell-group">
          <van-cell 
            title="数据备份" 
            is-link
            @click="backupData"
            icon="down"
            class="feature-cell"
          >
            <template #label>
              <span class="cell-desc">备份您的所有核对事项数据</span>
            </template>
          </van-cell>
          <van-cell 
            title="数据恢复" 
            is-link
            @click="showRestorePopup = true"
            icon="upgrade"
            class="feature-cell"
          >
            <template #label>
              <span class="cell-desc">从备份中恢复数据</span>
            </template>
          </van-cell>
          <van-cell 
            title="清空数据" 
            is-link
            @click="showClearConfirm"
            icon="delete"
            class="feature-cell danger"
          >
            <template #label>
              <span class="cell-desc">删除所有核对事项数据</span>
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <!-- 通用设置 -->
      <div class="section">
        <div class="section-header">通用设置</div>
        <van-cell-group inset class="cell-group">
          <van-cell title="深色模式" class="feature-cell">
            <template #right-icon>
              <van-switch 
                v-model="settings.darkMode" 
                size="24" 
                @change="(value) => saveSettings('darkMode', value)"
              />
            </template>
          </van-cell>
          <van-cell title="自动备份" class="feature-cell">
            <template #right-icon>
              <van-switch 
                v-model="settings.autoBackup" 
                size="24"
                @change="(value) => saveSettings('autoBackup', value)"
              />
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <!-- 关于 -->
      <div class="section">
        <div class="section-header">关于</div>
        <van-cell-group inset class="cell-group">
          <van-cell 
            title="当前版本" 
            value="1.0.0"
            class="feature-cell"
          />
          <van-cell 
            title="隐私政策" 
            is-link
            class="feature-cell"
          />
          <van-cell 
            title="用户协议" 
            is-link
            class="feature-cell"
          />
        </van-cell-group>
      </div>

      <!-- 退出登录 -->
      <div class="logout-section">
        <van-button 
          block 
          plain 
          type="danger" 
          class="logout-btn"
          @click="showLogoutConfirm"
        >
          退出登录
        </van-button>
      </div>
    </div>

    <!-- 恢复数据弹窗 -->
    <van-popup
      v-model:show="showRestorePopup"
      round
      position="bottom"
      class="restore-popup"
    >
      <div class="popup-header">
        <div class="popup-title">恢复数据</div>
        <van-icon name="cross" @click="showRestorePopup = false" />
      </div>
      <van-form @submit="restoreData">
        <van-field
          v-model="restoreCode"
          label="恢复码"
          placeholder="请输入备份时获得的恢复码"
          :rules="[{ required: true, message: '请输入恢复码' }]"
        />
        <div class="popup-footer">
          <van-button round block type="primary" native-type="submit">
            开始恢复
          </van-button>
        </div>
      </van-form>
    </van-popup>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import AV from 'leancloud-storage'
import { showToast, showDialog } from 'vant'

export default {
  name: 'Settings',
  setup() {
    const router = useRouter()
    const store = useStore()
    const showRestorePopup = ref(false)
    const restoreCode = ref('')
    const currentUser = computed(() => AV.User.current())

    const settings = ref({
      darkMode: false,
      autoBackup: false
    })

    const loadSettings = async () => {
      const currentUser = AV.User.current()
      if (currentUser) {
        const userSettings = currentUser.get('settings')
        if (userSettings) {
          settings.value = { ...userSettings }
        }
      }
    }

    const saveSettings = async (key, value) => {
      const currentUser = AV.User.current()
      if (currentUser) {
        try {
          settings.value[key] = value
          currentUser.set('settings', settings.value)
          await currentUser.save()
          showToast('设置已保存')
        } catch (error) {
          settings.value[key] = !value
          showToast('保存失败')
        }
      }
    }

    onMounted(() => {
      loadSettings()
    })

    const backupData = async () => {
      try {
        // 获取当前用户的所有数据
        const query = new AV.Query('ChecklistItem')
        query.equalTo('user', AV.User.current())
        query.include('category')
        const items = await query.find()
        
        // 生成备份数据
        const backupData = {
          items: items.map(item => ({
            title: item.get('title'),
            description: item.get('description'),
            steps: item.get('steps'),
            category: item.get('category')?.get('name'),
            completed: item.get('completed'),
            createdAt: item.createdAt
          })),
          timestamp: new Date().toISOString()
        }

        // 生成备份码（使用时间戳和随机字符串）
        const backupCode = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
        
        // 将备份数据存储到当前用户
        const currentUser = AV.User.current()
        currentUser.set('backupData', backupData)
        currentUser.set('backupCode', backupCode)
        await currentUser.save()
        
        showDialog({
          title: '备份成功',
          message: `您的恢复码是：${backupCode}\n请妥善保管此码，用于数据恢复。`,
          theme: 'round-button',
          confirmButtonText: '复制并关闭',
          confirmButtonColor: '#007AFF'
        }).then(() => {
          navigator.clipboard.writeText(backupCode)
          showToast('已复制到剪贴板')
        })
      } catch (error) {
        console.error('备份失败:', error)
        showToast('备份失败')
      }
    }

    const restoreData = async () => {
      if (!restoreCode.value) {
        showToast('请输入恢复码')
        return
      }

      try {
        // 从当前用户获取备份数据
        const currentUser = AV.User.current()
        const backupData = currentUser.get('backupData')
        const storedBackupCode = currentUser.get('backupCode')

        if (!backupData || storedBackupCode !== restoreCode.value) {
          showToast('恢复码无效')
          return
        }

        // 清除现有数据
        const query = new AV.Query('ChecklistItem')
        query.equalTo('user', currentUser)
        const existingItems = await query.find()
        await AV.Object.destroyAll(existingItems)

        // 恢复数据
        const ChecklistItem = AV.Object.extend('ChecklistItem')
        const Category = AV.Object.extend('Category')

        for (const itemData of backupData.items) {
          const item = new ChecklistItem()
          
          // 如果有分类，先查找或创建分类
          let category = null
          if (itemData.category) {
            const categoryQuery = new AV.Query('Category')
            categoryQuery.equalTo('name', itemData.category)
            categoryQuery.equalTo('user', currentUser)
            category = await categoryQuery.first()
            
            if (!category) {
              category = new Category()
              category.set('name', itemData.category)
              category.set('user', currentUser)
              await category.save()
            }
          }

          item.set({
            title: itemData.title,
            description: itemData.description,
            steps: itemData.steps,
            category: category,
            completed: itemData.completed,
            user: currentUser
          })

          await item.save()
        }
        
        showToast('数据恢复成功')
        showRestorePopup.value = false
        restoreCode.value = ''
        
        // 刷新数据
        await store.dispatch('fetchChecklistItems')
        await store.dispatch('fetchCategories')
      } catch (error) {
        console.error('恢复失败:', error)
        showToast('恢复失败')
      }
    }

    const showClearConfirm = () => {
      showDialog({
        title: '确认清空数据',
        message: '此操作将删除所有核对事项数据，且不可恢复，是否继续？',
        theme: 'round-button',
        showCancelButton: true,
        confirmButtonText: '确认清空',
        confirmButtonColor: '#ff2d55'
      }).then(() => {
        clearData()
      })
    }

    const clearData = async () => {
      try {
        const currentUser = AV.User.current()
        const query = new AV.Query('ChecklistItem')
        query.equalTo('user', currentUser)
        const items = await query.find()
        await AV.Object.destroyAll(items)
        showToast('数据已清空')
      } catch (error) {
        showToast('操作失败')
      }
    }

    const showLogoutConfirm = () => {
      showDialog({
        title: '确认退出',
        message: '是否确认退出登录？',
        theme: 'round-button',
        showCancelButton: true,
        confirmButtonText: '退出登录',
        confirmButtonColor: '#ff2d55'
      }).then(() => {
        logout()
      })
    }

    const logout = async () => {
      try {
        await AV.User.logOut()
        router.push('/login')
      } catch (error) {
        showToast('退出失败')
      }
    }

    return {
      currentUser,
      settings,
      showRestorePopup,
      restoreCode,
      backupData,
      restoreData,
      showClearConfirm,
      showLogoutConfirm,
      saveSettings
    }
  }
}
</script>

<style scoped>
.settings {
  min-height: 100vh;
  background-color: #f5f5f7;
  padding-bottom: env(safe-area-inset-bottom);
}

.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: rgba(245, 245, 247, 0.9);
  backdrop-filter: blur(20px);
  padding: 16px 20px;
}

.title {
  font-size: 34px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
}

.content {
  padding: 12px 0;
}

.section {
  margin-bottom: 24px;
}

.section-header {
  padding: 0 32px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #86868b;
  text-transform: uppercase;
}

.cell-group {
  margin: 0 16px;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
}

.user-cell {
  padding: 12px 16px;
}

.avatar {
  width: 44px;
  height: 44px;
  background: #f5f5f7;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.feature-cell {
  padding: 14px 16px;
}

.feature-cell.danger {
  color: #ff2d55;
}

.cell-desc {
  color: #86868b;
  font-size: 12px;
}

:deep(.van-cell__title) {
  font-size: 16px;
  font-weight: 400;
}

:deep(.van-switch) {
  font-size: 24px;
}

:deep(.van-switch__node) {
  background: #fff;
  box-shadow: 0 3px 1px 0 rgba(0, 0, 0, 0.05), 0 2px 2px 0 rgba(0, 0, 0, 0.1), 0 3px 3px 0 rgba(0, 0, 0, 0.05);
}

.logout-section {
  padding: 32px 16px;
}

.logout-btn {
  height: 50px;
  font-size: 17px;
  border-radius: 12px;
  color: #ff2d55;
  border-color: #ff2d55;
}

.restore-popup {
  padding: 20px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.popup-title {
  font-size: 18px;
  font-weight: 600;
}

.popup-footer {
  margin-top: 24px;
}

:deep(.van-dialog) {
  border-radius: 14px;
  overflow: hidden;
}

:deep(.van-dialog__header) {
  padding: 20px 24px 0;
  font-weight: 600;
  font-size: 17px;
}

:deep(.van-dialog__message) {
  padding: 16px 24px;
}

:deep(.van-dialog__footer) {
  padding: 8px 12px 20px;
}

:deep(.van-button--round) {
  border-radius: 12px;
}
</style> 